import React, { useEffect, useState } from 'react';
import { type AppSettings, type PerimeterConfig, type SettingsSection, type Suspect, type UpdatePerimeterBody } from '../api/types.js';
import { updateSettingsSection } from '../api/index.js';
import GeneralTab from './settings/GeneralTab.js';
import MissionTab from './settings/MissionTab.js';
import AlertThresholdsTab from './settings/AlertThresholdsTab.js';
import NotificationsTab from './settings/NotificationsTab.js';
import AccessTab from './settings/AccessTab.js';
import MyAccountTab from './settings/MyAccountTab.js';
// import DataSourcesTab from './settings/DataSourcesTab.js';
import AccountsTab from './settings/AccountsTab.js';
import { can } from '../../shared/types/index.js';
import { useSession } from '../session/SessionProvider.js';
import AuditTab from './settings/AuditTab.js';
import SystemStatusTab from './settings/SystemStatusTab.js';
import { useSettings } from '../SettingsProvider.js';
import { useTranslation } from '../i18n/useTranslation.js';

interface SettingsScreenProps {
  open: boolean;
  onClose: () => void;
  onLogout: () => void;
  onSave: (sectionLabel?: string) => void;
  suspects: Record<string, Suspect>;
  perimeters?: PerimeterConfig[];
  onPerimeterUpdate: (id: string, perimeter: UpdatePerimeterBody) => Promise<void>;
}

interface TabDef {
  id: string;
  icon: string;
  label: string;
  group: 'ops' | 'system' | 'security' | 'account';
  badge?: string;
}

function SettingsScreen({
  open,
  onClose,
  onLogout,
  onSave,
  suspects,
  perimeters,
  onPerimeterUpdate,
}: SettingsScreenProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('general');
  const { settings, setSettings } = useSettings();
  // The effective role: an admin impersonating in edit mode sees the operator tabs for the target's data.
  const { effectiveRole: role } = useSession();

  // Tabs that change shared configuration need the operator role; accounts need admin.
  const ALL_TABS: ReadonlyArray<TabDef & { visible: boolean }> = [
    { id: 'general', icon: '⚙', label: t('settings.tabGeneral'), group: 'ops', visible: can(role, 'systemSettings') },
    { id: 'mission', icon: '🎯', label: t('settings.tabMission'), group: 'ops', visible: can(role, 'systemSettings') },
    { id: 'alerts', icon: '🔔', label: t('settings.tabAlerts'), group: 'ops', visible: can(role, 'systemSettings') },
    // { id: 'datasources', icon: '📡', label: t('settings.tabDatasources'), group: 'system' },
    { id: 'notifications', icon: '📢', label: t('settings.tabNotifications'), group: 'system', visible: can(role, 'systemSettings') },
    { id: 'users', icon: '👥', label: t('settings.tabUsers'), group: 'system', visible: can(role, 'manageAccounts') },
    { id: 'access', icon: '🔐', label: t('settings.tabAccess'), group: 'security', visible: can(role, 'systemSettings') },
    { id: 'audit', icon: '📋', label: t('settings.tabAudit'), group: 'security', visible: true },
    { id: 'status', icon: '🟢', label: t('settings.tabStatus'), group: 'security', visible: true },
    { id: 'myaccount', icon: '👤', label: t('settings.tabMyaccount'), group: 'account', visible: true },
  ];
  const TABS: ReadonlyArray<TabDef> = ALL_TABS.filter((tab) => tab.visible);

  // Fall back to the first visible tab when the current one is not available to this role.
  useEffect(() => {
    if (TABS.length && !TABS.some((tab) => tab.id === activeTab)) setActiveTab(TABS[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TABS is rebuilt every render; role is what matters
  }, [role, activeTab]);

  const GROUP_LABELS: Record<TabDef['group'], string> = {
    ops: t('settings.groupOps'),
    system: t('settings.groupSystem'),
    security: t('settings.groupSecurity'),
    account: t('settings.groupAccount'),
  };

  useEffect(() => {
    if (!perimeters?.length || !settings) return;

    settings.alerts.perimeters.critical.distanceM = perimeters[0].perimeters[0].upperBound;
    settings.alerts.perimeters.alert.distanceM = perimeters[0].perimeters[1].upperBound;
    settings.alerts.perimeters.caution.distanceM = perimeters[0].perimeters[2].upperBound;
    settings.alerts.perimeters.watch.distanceM = perimeters[0].perimeters[3].upperBound;
  }, [perimeters, settings]);

  const handleSectionSave = async <K extends SettingsSection>(section: K, next: AppSettings[K]) => {
    if (
      section === 'alerts' &&
      perimeters &&
      ((next as AppSettings['alerts']).perimeters.critical.distanceM !== perimeters[0].perimeters[0].upperBound ||
        (next as AppSettings['alerts']).perimeters.alert.distanceM !== perimeters[0].perimeters[1].upperBound ||
        (next as AppSettings['alerts']).perimeters.caution.distanceM !== perimeters[0].perimeters[2].upperBound ||
        (next as AppSettings['alerts']).perimeters.watch.distanceM !== perimeters[0].perimeters[3].upperBound)
    ) {
      const data: UpdatePerimeterBody = {
        perimeters: [
          { upperBound: (next as AppSettings['alerts']).perimeters.critical.distanceM },
          { upperBound: (next as AppSettings['alerts']).perimeters.alert.distanceM },
          { upperBound: (next as AppSettings['alerts']).perimeters.caution.distanceM },
          { upperBound: (next as AppSettings['alerts']).perimeters.watch.distanceM },
        ],
      };
      await onPerimeterUpdate(perimeters[0].id, data);
    }

    const updated = await updateSettingsSection(section, next);
    setSettings(updated);
    onSave(TABS.find((t) => t.id === section)?.label);
  };

  // Group tabs by section for the sidebar
  const groups: Array<{ group: TabDef['group']; tabs: Array<TabDef> }> = [];
  for (const t of TABS) {
    const last = groups[groups.length - 1];
    if (last && last.group === t.group) last.tabs.push(t);
    else groups.push({ group: t.group, tabs: [t] });
  }

  return (
    <div className={`settings-overlay${open ? ' open' : ''}`} style={{ display: open ? 'flex' : 'none' }}>
      <div className="settings-nav">
        <div>
          <div className="settings-title">{t('settings.title')}</div>
          <div className="settings-subtitle">{t('settings.subtitle')}</div>
        </div>
        <div className="settings-close" onClick={onClose}>
          {t('settings.close')}
        </div>
      </div>
      <div className="settings-body">
        <div className="settings-sidebar">
          {groups.map((g, i) => (
            <React.Fragment key={g.group}>
              <div className="stab section">{GROUP_LABELS[g.group]}</div>
              {g.tabs.map((t) => (
                <div key={t.id} className={`stab ${activeTab === t.id ? 'on' : ''}`} onClick={() => setActiveTab(t.id)}>
                  <span className="stab-icon">{t.icon}</span>
                  {t.label}
                  {t.badge && <span className="stab-badge">{t.badge}</span>}
                </div>
              ))}
            </React.Fragment>
          ))}
          <div style={{ flexGrow: 1 }} />
          <div className="stab danger" onClick={onLogout}>
            {t('settings.signOut')}
          </div>
        </div>

        <div className="settings-content">
          {!settings && (
            <div className="sp on">
              <div className="sp-title">{t('settings.unavailable')}</div>
            </div>
          )}

          {settings && (
            <>
              {activeTab === 'general' && (
                <GeneralTab value={settings.general} onSave={(v) => handleSectionSave('general', v)} />
              )}
              {activeTab === 'mission' && (
                <MissionTab value={settings.mission} onSave={(v) => handleSectionSave('mission', v)} />
              )}
              {activeTab === 'alerts' && (
                <AlertThresholdsTab value={settings.alerts} onSave={(v) => handleSectionSave('alerts', v)} />
              )}
              {activeTab === 'notifications' && (
                <NotificationsTab
                  value={settings.notifications}
                  onSave={(v) => handleSectionSave('notifications', v)}
                />
              )}
              {activeTab === 'access' && (
                <AccessTab value={settings.access} onSave={(v) => handleSectionSave('access', v)} />
              )}
              {/* {activeTab === 'datasources' && (
                <DataSourcesTab value={settings.datasources} onSave={(v) => handleSectionSave('datasources', v)} />
              )} */}
              {activeTab === 'myaccount' && (
                <MyAccountTab
                  value={settings.myaccount}
                  onSave={(v) => handleSectionSave('myaccount', v)}
                  onSignOutAll={onLogout}
                />
              )}
              {activeTab === 'users' && <AccountsTab onImpersonated={onClose} />}
              {activeTab === 'audit' && <AuditTab suspects={suspects} />}
              {activeTab === 'status' && <SystemStatusTab />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingsScreen;
